import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc, setDoc, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import UserModal from '../components/forms/UserModal';
import ReassignmentModal from '../components/forms/ReassignmentModal';
import * as XLSX from 'xlsx';
import { exportData } from '../utils/exportUtils';
import ExportButton from '../components/common/ExportButton';
import Papa from 'papaparse';
import { importAndSyncCollectionFromCSV } from '../utils/importUtils';
import { formatDate } from '../utils/dateHelpers';
import { collectionPath } from '../utils/firestoreCollections';
import RegisterForm from '../components/forms/RegisterForm';
import PermissionManager from '../components/forms/PermissionManager';
import { usePermissions } from '../hooks/usePermissions';
import { DEFAULT_ROLE_PERMISSIONS, getUserPermissions, FEATURE_PERMISSIONS } from '../utils/permissions';
import Toast from '../components/common/Toast';
import Tooltip from '../components/common/Tooltip';
import { useDebounce } from '../hooks/useDebounce';
import { search as typesenseSearch, isTypesenseAvailable } from '../utils/typesense';
import { useNavigate } from 'react-router-dom';
import { logEntityChanges } from '../utils/logger';
import { API_URL } from '../utils/apiConfig';

const USERS_PAGE_SIZE = 25;

const getFullName = (user) => user.full_name || [user.firstName, user.lastName].filter(Boolean).join(' ');

const UserAdminPage = ({ user, uid, onImpersonate }) => {
    const { can, rolePermissions: allRolePermissions } = usePermissions(user);
    const navigate = useNavigate();
    const [usersList, setUsersList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editUser, setEditUser] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 350);
    const [typesenseEnabled, setTypesenseEnabled] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [showExportMenu, setShowExportMenu] = useState(false);
    const [importing, setImporting] = useState(false);
    const [importCancelled, setImportCancelled] = useState(false);
    const [importTimeoutId, setImportTimeoutId] = useState(null);
    const importCancelledRef = React.useRef(false);
    const [sortBy, setSortBy] = useState('firstName');
    const [sortDir, setSortDir] = useState('asc');     // or 'desc' if you prefer
    const importInputRef = React.useRef();
    const [showRegisterModal, setShowRegisterModal] = useState(false);
    const [showPermissionsModal, setShowPermissionsModal] = useState(false);
    const [selectedUserForPermissions, setSelectedUserForPermissions] = useState(null);
    const [toast, setToast] = useState(null);
    const [syncing, setSyncing] = useState(false);
    const [showDeactivated, setShowDeactivated] = useState(false);
    const [roleNamesMap, setRoleNamesMap] = useState({});
    const [deactivatingUser, setDeactivatingUser] = useState(null); // user pending deactivation (shows ReassignmentModal)

    // Load role display names from Firestore
    useEffect(() => {
        const loadRoleNames = async () => {
            try {
                const rolesSnap = await getDocs(collection(db, collectionPath('rolePermissions')));
                const builtIn = { admin: 'Admin', adminViewOnly: 'Admin - View Only', manager: 'Manager', marketing: 'Marketing', user: 'User', disabled: 'Disabled' };
                const map = { ...builtIn };
                rolesSnap.docs.forEach(d => {
                    map[d.id] = d.data().name || builtIn[d.id] || d.id;
                });
                setRoleNamesMap(map);
            } catch (err) {
                console.error('Error loading role names:', err);
            }
        };
        loadRoleNames();
    }, []);

    const getRoleDisplayName = (roleId) => roleNamesMap[roleId] || roleId?.charAt(0).toUpperCase() + roleId?.slice(1) || '-';

    // Check Typesense availability
    useEffect(() => {
        const checkTypesense = async () => {
            const available = await isTypesenseAvailable();
            setTypesenseEnabled(available);
            if (available) {
                console.log('[UserAdminPage] Typesense available for search');
            }
        };
        checkTypesense();
    }, []);

    // Typesense search when search term changes
    useEffect(() => {
        if (!typesenseEnabled || !debouncedSearch.trim()) {
            setSearchResults([]);
            return;
        }

        const performSearch = async () => {
            setSearching(true);
            try {
                const startTime = performance.now();
                const response = await typesenseSearch('users', {
                    q: debouncedSearch,
                    query_by: 'displayName,firstName,lastName,email',
                    per_page: 250 // Increased from 100 for better coverage
                });

                const results = response.hits.map(hit => ({
                    uid: hit.document.id,
                    ...hit.document
                }));

                const duration = performance.now() - startTime;
                console.log(`[UserAdminPage] 🚀 Typesense found ${results.length} users in ${Math.round(duration)}ms`);
                setSearchResults(results);
            } catch (error) {
                console.error('[UserAdminPage] Typesense search error:', error);
                setSearchResults([]);
            } finally {
                setSearching(false);
            }
        };

        performSearch();
    }, [debouncedSearch, typesenseEnabled]);

    useEffect(() => {
        if (!user || !can(FEATURE_PERMISSIONS.USER_VIEW_ALL)) return;
        const unsub = onSnapshot(
            query(
                collection(db, collectionPath('users')),
                // Remove orderBy and limit - we'll sort client-side
            ),
            (snap) => {
                setUsersList(snap.docs.map(doc => ({ uid: doc.id, ...doc.data() })));
                setLoading(false);
            },
            (err) => {
                setError('Failed to load users');
                setLoading(false);
            }
        );
        return () => unsub();
    }, [user]);

    const handleEdit = (u) => {
        setEditUser(u);
        setShowModal(true);
    };

    const handleAdd = () => {
        setShowRegisterModal(true);
    };

    // Handle viewing/editing user permissions
    const handleViewPermissions = (u) => {
        setSelectedUserForPermissions(u);
        setShowPermissionsModal(true);
    };

    // Handle sync from ApparelMagic
    const handleSyncFromAM = async () => {
        if (!can(FEATURE_PERMISSIONS.USER_CREATE)) {
            setToast({ message: 'You do not have permission to sync users', type: 'error' });
            return;
        }

        const confirmed = window.confirm(
            "Sync users from ApparelMagic?\n\n" +
            "This will:\n" +
            "• Fetch and sync all salespeople from ApparelMagic\n" +
            "• Create Firebase Auth accounts with random passwords\n" +
            "• Send admin email notification when complete\n\n" +
            "This may take 10-30 seconds. Continue?"
        );

        if (!confirmed) return;

        setSyncing(true);
        setToast({ message: 'Sync started... This may take up to 60 seconds', type: 'info' });

        try {
            // Local development uses localhost:4000; production uses the configured VPS API URL.
            const syncUrl = API_URL
                ? `${API_URL.replace(/\/$/, '')}/api/sync-users`
                : '/api/sync-users';
            const response = await fetch(syncUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ uid: user.uid })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Sync request failed');
            }

            const result = await response.json();
            
            if (result.message) {
                // Background processing
                setToast({ 
                    message: result.message, 
                    type: 'info' 
                });
            } else {
                // Immediate result
                setToast({ 
                    message: `Sync completed! ${result.usersWritten || 0} users synced, ${result.authCreated || 0} new Auth accounts created.`, 
                    type: 'success' 
                });
            }
            
            // Refresh user list after a delay
            setTimeout(() => {
                window.location.reload();
            }, 2000);
        } catch (error) {
            console.error('Sync error:', error);
            const message = error instanceof TypeError && error.message === 'Failed to fetch'
                ? `Cannot reach the API server at ${API_URL || 'the configured API URL'}. In local development, start it with "npm run server"; for production, verify the VPS server is running.`
                : `Sync failed: ${error.message}. Check server logs or contact administrator.`;
            setToast({
                message,
                type: 'error'
            });
        } finally {
            setSyncing(false);
        }
    };

    // Handle permission override change
    const handlePermissionChange = async (permission, action) => {
        if (!selectedUserForPermissions) return;

        const userRef = doc(db, collectionPath('users'), selectedUserForPermissions.uid);
        
        try {
            // Get role permissions to check for redundant grants/denials
            const rolePerms = allRolePermissions[selectedUserForPermissions.role] || [];
            
            let updatedGrants = [...(selectedUserForPermissions.permissionGrants || [])];
            let updatedDenials = [...(selectedUserForPermissions.permissionDenials || [])];

            if (action === 'grant') {
                // Add to grants ONLY if not already in role
                if (!rolePerms.includes(permission) && !updatedGrants.includes(permission)) {
                    updatedGrants.push(permission);
                }
                // Remove from denials (in case it was denied)
                updatedDenials = updatedDenials.filter(p => p !== permission);
            } else if (action === 'deny') {
                // Add to denials ONLY if it's in the role (otherwise it's already not accessible)
                if (rolePerms.includes(permission) && !updatedDenials.includes(permission)) {
                    updatedDenials.push(permission);
                }
                // Remove from grants (in case it was explicitly granted)
                updatedGrants = updatedGrants.filter(p => p !== permission);
            } else if (action === 'default') {
                // Remove from both (revert to role default)
                updatedGrants = updatedGrants.filter(p => p !== permission);
                updatedDenials = updatedDenials.filter(p => p !== permission);
            }

            // Clean up: Remove grants that are already in role
            updatedGrants = updatedGrants.filter(p => !rolePerms.includes(p));
            
            // Clean up: Remove denials that are not in role (redundant)
            updatedDenials = updatedDenials.filter(p => rolePerms.includes(p));

            await updateDoc(userRef, {
                permissionGrants: updatedGrants,
                permissionDenials: updatedDenials,
            });

            // Audit log: permission override change
            await logEntityChanges({
                oldObj: {
                    permissionGrants: selectedUserForPermissions.permissionGrants || [],
                    permissionDenials: selectedUserForPermissions.permissionDenials || [],
                },
                newObj: {
                    permissionGrants: updatedGrants,
                    permissionDenials: updatedDenials,
                },
                fields: ['permissionGrants', 'permissionDenials'],
                entityType: 'User',
                entityId: selectedUserForPermissions.uid,
                user,
                action: `Permission ${action === 'grant' ? 'Granted' : action === 'deny' ? 'Denied' : 'Reset to Role Default'}`,
                extraDetails: `Permission "${permission}" ${action === 'grant' ? 'granted to' : action === 'deny' ? 'denied for' : 'reset to role default for'} ${selectedUserForPermissions.firstName || ''} ${selectedUserForPermissions.lastName || ''} (${selectedUserForPermissions.email || selectedUserForPermissions.uid})`.trim(),
            });

            // Update local state
            setSelectedUserForPermissions({
                ...selectedUserForPermissions,
                permissionGrants: updatedGrants,
                permissionDenials: updatedDenials,
            });

            setToast({ message: 'Permission updated', type: 'success' });
        } catch (error) {
            console.error('Error updating permission:', error);
            setToast({ message: 'Failed to update permission', type: 'error' });
        }
    };

    const handleBulkPermissionChange = async (permissions, action) => {
        if (!selectedUserForPermissions) return;

        const userRef = doc(db, collectionPath('users'), selectedUserForPermissions.uid);

        try {
            const rolePerms = allRolePermissions[selectedUserForPermissions.role] || [];
            let updatedGrants = [...(selectedUserForPermissions.permissionGrants || [])];
            let updatedDenials = [...(selectedUserForPermissions.permissionDenials || [])];

            for (const permission of permissions) {
                if (action === 'grant') {
                    if (!rolePerms.includes(permission) && !updatedGrants.includes(permission)) {
                        updatedGrants.push(permission);
                    }
                    updatedDenials = updatedDenials.filter(p => p !== permission);
                } else if (action === 'deny') {
                    if (rolePerms.includes(permission) && !updatedDenials.includes(permission)) {
                        updatedDenials.push(permission);
                    }
                    updatedGrants = updatedGrants.filter(p => p !== permission);
                } else if (action === 'default') {
                    updatedGrants = updatedGrants.filter(p => p !== permission);
                    updatedDenials = updatedDenials.filter(p => p !== permission);
                }
            }

            updatedGrants = updatedGrants.filter(p => !rolePerms.includes(p));
            updatedDenials = updatedDenials.filter(p => rolePerms.includes(p));

            await updateDoc(userRef, {
                permissionGrants: updatedGrants,
                permissionDenials: updatedDenials,
            });

            // Audit log: bulk permission change
            await logEntityChanges({
                oldObj: {
                    permissionGrants: selectedUserForPermissions.permissionGrants || [],
                    permissionDenials: selectedUserForPermissions.permissionDenials || [],
                },
                newObj: {
                    permissionGrants: updatedGrants,
                    permissionDenials: updatedDenials,
                },
                fields: ['permissionGrants', 'permissionDenials'],
                entityType: 'User',
                entityId: selectedUserForPermissions.uid,
                user,
                action: `Bulk Permission ${action === 'grant' ? 'Grant' : action === 'deny' ? 'Deny' : 'Reset'}`,
                extraDetails: `${permissions.length} permission(s) ${action === 'grant' ? 'granted to' : action === 'deny' ? 'denied for' : 'reset to role default for'} ${selectedUserForPermissions.firstName || ''} ${selectedUserForPermissions.lastName || ''} (${selectedUserForPermissions.email || selectedUserForPermissions.uid})`.trim(),
            });

            setSelectedUserForPermissions({
                ...selectedUserForPermissions,
                permissionGrants: updatedGrants,
                permissionDenials: updatedDenials,
            });

            setToast({ message: 'Permissions updated', type: 'success' });
        } catch (error) {
            console.error('Error updating permissions:', error);
            setToast({ message: 'Failed to update permissions', type: 'error' });
        }
    };

    // Restore default permissions (clear all custom grants/denials)
    const handleRestoreDefaultPermissions = async () => {
        if (!selectedUserForPermissions) return;
        if (!window.confirm(`Restore default permissions for ${selectedUserForPermissions.firstName} ${selectedUserForPermissions.lastName}? This will remove all custom permission grants and denials, reverting to the standard "${selectedUserForPermissions.role}" role permissions.`)) return;

        const userRef = doc(db, collectionPath('users'), selectedUserForPermissions.uid);
        try {
            await updateDoc(userRef, {
                permissionGrants: [],
                permissionDenials: [],
            });

            // Audit log: restore to role defaults
            await logEntityChanges({
                oldObj: {
                    permissionGrants: selectedUserForPermissions.permissionGrants || [],
                    permissionDenials: selectedUserForPermissions.permissionDenials || [],
                },
                newObj: {
                    permissionGrants: [],
                    permissionDenials: [],
                },
                fields: ['permissionGrants', 'permissionDenials'],
                entityType: 'User',
                entityId: selectedUserForPermissions.uid,
                user,
                action: 'Restored Default Permissions',
                extraDetails: `All custom permission grants/denials cleared for ${selectedUserForPermissions.firstName || ''} ${selectedUserForPermissions.lastName || ''} (${selectedUserForPermissions.email || selectedUserForPermissions.uid}); reverted to standard "${selectedUserForPermissions.role}" role permissions`.trim(),
            });

            setSelectedUserForPermissions({
                ...selectedUserForPermissions,
                permissionGrants: [],
                permissionDenials: [],
            });
            setToast({ message: 'Permissions restored to role defaults', type: 'success' });
        } catch (error) {
            console.error('Error restoring default permissions:', error);
            setToast({ message: 'Failed to restore default permissions', type: 'error' });
        }
    };

    // Handle add/edit/deactivate/reactivate from UserModal
    const handleModalClose = async (result) => {
        setShowModal(false);
        setEditUser(null);
        if (!result) return;

        // Deactivate user: open ReassignmentModal to check for tasks/opps first
        if (result.deactivate && result.id) {
            const targetUser = usersList.find(u => (u.uid || u.id) === result.id) || result;
            setDeactivatingUser(targetUser);
            return;
        }

        // Reactivate user: enable in Firebase Auth + restore previous role + unpause subscriptions
        if (result.reactivate && result.id) {
            try {
                const setUserDisabled = httpsCallable(getFunctions(), 'setUserDisabled');
                await setUserDisabled({ uid: result.uid || result.id, disabled: false });
                const existingUser = usersList.find(u => u.id === result.id);
                const restoredRole = existingUser?.previousRole || 'user';
                await setDoc(doc(db, collectionPath('users', result.id)), {
                    role: restoredRole,
                    status: 'Active',
                    previousRole: null,
                    deactivatedAt: null,
                    deactivatedBy: null,
                    reactivatedAt: new Date().toISOString(),
                    reactivatedBy: user?.displayName || user?.email || 'admin'
                }, { merge: true });

                // Audit log: reactivation (role restore + status change)
                await logEntityChanges({
                    oldObj: existingUser || { role: 'disabled', status: 'Deactivated' },
                    newObj: { ...(existingUser || {}), role: restoredRole, status: 'Active' },
                    fields: ['role', 'status'],
                    entityType: 'User',
                    entityId: result.id,
                    user,
                    action: 'Reactivated User',
                    extraDetails: `User reactivated${existingUser?.previousRole ? `; role restored to "${restoredRole}"` : `; role defaulted to "${restoredRole}" (no previous role recorded)`}`,
                });

                // Unpause subscriptions that were paused by deactivation
                const subsQuery = query(
                    collection(db, collectionPath('reportSubscriptions')),
                    where('userId', '==', result.uid || result.id),
                    where('pausedByDeactivation', '==', true)
                );
                const subsSnap = await getDocs(subsQuery);
                const unpausePromises = subsSnap.docs.map(d => updateDoc(doc(db, collectionPath('reportSubscriptions'), d.id), {
                    isPaused: false,
                    pauseReason: null,
                    pausedByDeactivation: null
                }));
                await Promise.all(unpausePromises);

                const unpausedCount = unpausePromises.length;
                setToast({ message: `User reactivated with role: ${restoredRole}${unpausedCount > 0 ? ` and ${unpausedCount} subscription${unpausedCount !== 1 ? 's' : ''} resumed` : ''}`, type: 'success' });
            } catch (err) {
                console.error('Error reactivating user:', err);
                setToast({ message: `Failed to reactivate user: ${err.message}`, type: 'error' });
            }
            return;
        }

        // Legacy delete support (e.g. from CSV import)
        if (result.delete && result.id) {
            await deleteDoc(doc(db, collectionPath('users', result.id)));
            return;
        }

        // Add or update user
        const userData = { ...result };
        if (result.id) {
            await setDoc(doc(db, collectionPath('users', result.id)), userData, { merge: true });
        } else {
            await addDoc(collection(db, collectionPath('users')), userData);
        }
    };

    // Count deactivated users for the toggle badge
    const deactivatedCount = usersList.filter(u => u.role === 'disabled').length;

    // Use Typesense results if available and searching, otherwise use Firestore + client-side filter
    const filteredUsersList = ((typesenseEnabled && search.trim() && searchResults.length > 0)
        ? searchResults
        : usersList.filter(u => {
            if (!search) return true;
            const searchLower = search.toLowerCase();
            return [
                getFullName(u),
                u.firstName,
                u.lastName,
                u.displayName,
                u.email,
                u.phone,
                u.role
            ]
                .map(val => (val || '').toString().toLowerCase())
                .some(val => val.includes(searchLower));
        })).filter(u => showDeactivated || u.role !== 'disabled');

    const handleSort = (col) => {
        if (sortBy === col) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(col);
            setSortDir('asc');
        }
    };

    // Handle reassignment confirmation from ReassignmentModal
    const handleReassignmentConfirm = async ({ reassignToUid, reassignToName, tasks, opportunities, skipReassignment }) => {
        const targetUser = deactivatingUser;
        const targetId = targetUser?.uid || targetUser?.id;
        setDeactivatingUser(null);

        try {
            // 1. Reassign tasks and opportunities if not skipped
            if (!skipReassignment && reassignToUid) {
                const reassignPromises = [];

                // Reassign open tasks
                for (const task of tasks) {
                    reassignPromises.push(
                        updateDoc(doc(db, collectionPath('tasks'), task.id), {
                            uid: reassignToUid,
                            assignedTo: reassignToName,
                            reassignedFrom: targetId,
                            reassignedAt: new Date().toISOString(),
                            reassignedBy: user?.displayName || user?.email || 'admin'
                        })
                    );
                }

                // Reassign open opportunities
                for (const opp of opportunities) {
                    const updateData = {
                        uid: reassignToUid,
                        reassignedFrom: targetId,
                        reassignedAt: new Date().toISOString(),
                        reassignedBy: user?.displayName || user?.email || 'admin'
                    };
                    // Update salesReps array: replace the deactivated user with the new user
                    if (Array.isArray(opp.salesReps)) {
                        updateData.salesReps = opp.salesReps.map(rep =>
                            rep.salesRepId === targetId
                                ? { ...rep, salesRepId: reassignToUid, salesRepName: reassignToName, displayName: reassignToName }
                                : rep
                        );
                        updateData.salesRepNames = updateData.salesReps.map(r => r.displayName || r.salesRepName);
                    }
                    reassignPromises.push(
                        updateDoc(doc(db, collectionPath('opportunities'), opp.id), updateData)
                    );
                }

                await Promise.all(reassignPromises);
            }

            // 2. Disable Firebase Auth
            const setUserDisabled = httpsCallable(getFunctions(), 'setUserDisabled');
            await setUserDisabled({ uid: targetId, disabled: true });

            // 3. Set role to disabled
            await setDoc(doc(db, collectionPath('users', targetId)), {
                role: 'disabled',
                status: 'Inactive',
                previousRole: targetUser?.role || 'user',
                deactivatedAt: new Date().toISOString(),
                deactivatedBy: user?.displayName || user?.email || 'admin'
            }, { merge: true });

            // 4. Pause all report subscriptions
            const subsQuery = query(
                collection(db, collectionPath('reportSubscriptions')),
                where('userId', '==', targetId)
            );
            const subsSnap = await getDocs(subsQuery);
            const pausePromises = subsSnap.docs
                .filter(d => !d.data().isPaused)
                .map(d => updateDoc(doc(db, collectionPath('reportSubscriptions'), d.id), {
                    isPaused: true,
                    pauseReason: 'User deactivated',
                    pausedByDeactivation: true
                }));
            await Promise.all(pausePromises);

            // Build summary message
            const parts = ['User deactivated'];
            if (!skipReassignment && reassignToUid) {
                const reassigned = tasks.length + opportunities.length;
                if (reassigned > 0) parts.push(`${reassigned} item${reassigned !== 1 ? 's' : ''} reassigned to ${reassignToName}`);
            }
            if (pausePromises.length > 0) parts.push(`${pausePromises.length} subscription${pausePromises.length !== 1 ? 's' : ''} paused`);
            setToast({ message: parts.join(', '), type: 'success' });
        } catch (err) {
            console.error('Error deactivating user:', err);
            setToast({ message: `Failed to deactivate user: ${err.message}`, type: 'error' });
        }
    };

    const handleReassignmentCancel = () => {
        setDeactivatingUser(null);
    };

    const sortedUsersList = [...filteredUsersList].sort((a, b) => {
        let valA = sortBy === 'full_name' ? getFullName(a) : a[sortBy] ?? '';
        let valB = sortBy === 'full_name' ? getFullName(b) : b[sortBy] ?? '';
        if (sortBy === 'never_match_this') {
            valA = Array.isArray(valA) ? valA.join(', ') : valA;
            valB = Array.isArray(valB) ? valB.join(', ') : valB;
        }
        valA = valA.toString().toLowerCase();
        valB = valB.toString().toLowerCase();
        if (valA < valB) return sortDir === 'asc' ? -1 : 1;
        if (valA > valB) return sortDir === 'asc' ? 1 : -1;
        return 0;
    });

    const handleExport = (format) => {
        const headers = [
            "Full Name", "Display Name", "Email", "Phone", "Role"
        ];
        const rowMapper = u => [
            getFullName(u),
            u.displayName || '',
            u.email || '',
            u.phone || '',
            u.role ? getRoleDisplayName(u.role) : ''
        ];
        
        exportData({
            filename: 'users',
            format,
            headers,
            data: sortedUsersList,
            rowMapper,
            options: format.toLowerCase() === 'pdf' ? {
                title: `Users Export - ${new Date().toLocaleDateString()}`,
                fontSize: 9,
                orientation: 'portrait',
                pageSize: 'a4'
            } : {
                title: `Users Export - ${new Date().toLocaleDateString()}`,
                sheetName: 'Users',
                columnWidths: [30, 20, 20, 30, 15, 10]
            }
        });
    };

    const handleImportCSV = async (e) => {
        const file = e.target.files[0];
        if (!file) {
            if (importInputRef.current) importInputRef.current.value = '';
            return;
        }
        
        // First warning
        const confirmed1 = window.confirm(
            "⚠️ DESTRUCTIVE IMPORT WARNING ⚠️\n\n" +
            "This operation will DELETE users not present in the CSV file.\n\n" +
            "Are you absolutely sure you want to continue?"
        );
        if (!confirmed1) {
            if (importInputRef.current) importInputRef.current.value = '';
            return;
        }
        
        // Second warning
        const confirmed2 = window.confirm(
            "🚨 FINAL WARNING 🚨\n\n" +
            "Users deleted by this import CANNOT be recovered.\n\n" +
            "Have you:\n" +
            "✓ Exported current users as backup?\n" +
            "✓ Verified the CSV file is correct?\n" +
            "✓ Understood this is IRREVERSIBLE?\n\n" +
            "Click OK only if you are 100% certain."
        );
        if (!confirmed2) {
            if (importInputRef.current) importInputRef.current.value = '';
            return;
        }
        
        setImporting(true);
        setImportCancelled(false);
        importCancelledRef.current = false;

        // Auto-cancel after 2 minutes (120000 ms)
        const timeoutId = setTimeout(() => {
            importCancelledRef.current = true;
            setImportCancelled(true);
            setImporting(false);
            alert('Import timed out and was cancelled.');
            if (importInputRef.current) importInputRef.current.value = '';
        }, 120000);
        setImportTimeoutId(timeoutId);

        try {
            const { added, updated, deleted, cancelled } = await importAndSyncCollectionFromCSV(
                file,
                db,
                undefined,
                { cancelled: () => importCancelledRef.current }
            );
            clearTimeout(timeoutId);
            setImportTimeoutId(null);
            setImporting(false);
            if (cancelled) {
                alert('Import was cancelled.');
            } else {
                alert(`Import complete! Added: ${added}, Updated: ${updated}, Deleted: ${deleted}`);
            }
        } catch (err) {
            clearTimeout(timeoutId);
            setImportTimeoutId(null);
            setImporting(false);
            alert('Failed to import CSV: ' + err.message);
        }
        // Always reset file input at the end
        if (importInputRef.current) importInputRef.current.value = '';
    };

    if (!user || !can(FEATURE_PERMISSIONS.USER_VIEW_ALL)) {
        return <div className="p-6">Access denied.</div>;
    }

    return (
        <main className="flex-grow p-6 overflow-y-auto">
            {toast && <Toast {...toast} onClose={() => setToast(null)} />}
            <h2 className="text-2xl font-bold mb-4">User Administration</h2>
            
            {/* Remove inline RegisterForm, use modal instead */}

            <div className="flex items-center gap-4 mb-4">
                <div className="flex items-center flex-1">
                    <span className="mr-2 font-medium text-gray-700">Search:</span>
                    <input
                        type="text"
                        placeholder="Search by name, email, etc..."
                        className={`border rounded px-3 py-2 w-full md:w-[28rem] transition-colors duration-150 ${
                            search ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'
                        }`}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>
                {deactivatedCount > 0 && (
                    <button
                        onClick={() => setShowDeactivated(!showDeactivated)}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded text-sm font-medium transition-colors ${
                            showDeactivated
                                ? 'bg-red-100 text-red-700 border border-red-300'
                                : 'bg-gray-100 text-gray-600 border border-gray-300 hover:bg-gray-200'
                        }`}
                    >
                        {showDeactivated ? '👁️ Showing' : '👁️‍🗨️ Show'} Deactivated
                        <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                            showDeactivated ? 'bg-red-200 text-red-800' : 'bg-gray-200 text-gray-700'
                        }`}>
                            {deactivatedCount}
                        </span>
                    </button>
                )}
            </div>
            <div className="flex items-center mb-4">
                <div className="flex items-center space-x-2">
                    {can(FEATURE_PERMISSIONS.USER_CREATE) && (
                    <button
                        className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-500"
                        onClick={handleAdd}
                        disabled={importing}
                    >
                        Add User
                    </button>
                    )}
                    
                    {/* Sync from ApparelMagic button - Admin Only */}
                    {can(FEATURE_PERMISSIONS.USER_CREATE) && (
                        <Tooltip text={
                            <div className="text-left">
                                <div className="font-semibold text-purple-300 mb-1">🔄 Sync from ApparelMagic</div>
                                <div className="text-sm">Fetch and sync salespeople from ApparelMagic</div>
                                <div className="text-sm text-purple-200 mt-1">Creates Firebase Auth accounts automatically</div>
                            </div>
                        } maxWidth={320}>
                            <button
                                className={`px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-500 ${(syncing || importing) ? 'pointer-events-none opacity-50' : ''}`}
                                onClick={handleSyncFromAM}
                                type="button"
                                disabled={syncing || importing}
                            >
                                {syncing ? 'Syncing...' : '🔄 Sync from AM'}
                            </button>
                        </Tooltip>
                    )}
                </div>
                
                <div className="flex-1"></div>
                
                <div className="flex items-center space-x-2">
                    {/* Export button */}
                    <ExportButton
                        onExport={handleExport}
                        formats={['csv', 'xlsx', 'pdf']}
                        disabled={importing}
                        tooltipText="Export all users in various formats (CSV, Excel, PDF)"
                        label="Export Users"
                        className="bg-green-600 hover:bg-green-700"
                    />
                    
                    {/* Destructive Import Button - Admin Only */}
                    {can(FEATURE_PERMISSIONS.USER_CREATE) && (<>
                    <input
                        ref={importInputRef}
                        type="file"
                        accept=".csv"
                        onChange={handleImportCSV}
                        style={{ display: 'none' }}
                    />
                    <Tooltip text={
                        <div className="text-left">
                            <div className="font-semibold text-red-300 mb-1">⚠️ DESTRUCTIVE IMPORT</div>
                            <div className="text-sm">Admin only: Replace entire user list with CSV data.</div>
                            <div className="text-sm text-red-200 mt-1">This DELETES users not in the file!</div>
                        </div>
                    } maxWidth={300}>
                        <button
                            className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50 flex items-center gap-2"
                            onClick={() => importInputRef.current?.click()}
                            disabled={importing}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                            </svg>
                            {importing ? 'Importing...' : 'Destructive Import'}
                        </button>
                    </Tooltip>
                    </>)}
                </div>
            </div>
            <div className="overflow-x-auto bg-white rounded-lg shadow">
                <table className="w-full whitespace-no-wrap">
                    <thead>
                        <tr className="text-xs font-semibold tracking-wide text-left text-gray-500 uppercase border-b bg-gray-50">
                            {[
                                { key: 'full_name', label: 'Full Name' },
                                { key: 'displayName', label: 'AM Display Name' },
                                { key: 'email', label: 'Email' },
                                { key: 'phone', label: 'Phone' },
                                { key: 'title', label: 'Title' },
                                { key: 'department', label: 'Department' },

                                { key: 'role', label: 'Role' },
                                { key: 'permissions', label: 'Permissions', sortable: false },
                                { key: 'actions', label: 'Actions', sortable: false }
                            ].map(col => (
                                <th
                                    key={col.key}
                                    className={`px-4 py-3 cursor-pointer select-none ${col.thClass || ''}`}
                                    onClick={col.sortable === false ? undefined : () => handleSort(col.key)}
                                >
                                    {col.label}
                                    {col.sortable === false ? null : (
                                        sortBy === col.key ? (
                                            <span className="ml-1">{sortDir === 'asc' ? '▲' : '▼'}</span>
                                        ) : null
                                    )}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={11} className="text-center py-4">Loading users...</td>
                            </tr>
                        ) : error ? (
                            <tr>
                                <td colSpan={11} className="text-center py-4 text-red-500">{error}</td>
                            </tr>
                        ) : sortedUsersList.length === 0 ? (
                            <tr>
                                <td colSpan={11} className="text-center py-4">No users found.</td>
                            </tr>
                        ) : (
                            sortedUsersList.map(u => {
                                const userPerms = getUserPermissions(u, allRolePermissions);
                                // Get role permissions for this user
                                const rolePerms = allRolePermissions[u.role] || [];
                                // Check if user has actual overrides (grants/denials that differ from role)
                                const actualGrants = (u.permissionGrants || []).filter(p => !rolePerms.includes(p));
                                const actualDenials = (u.permissionDenials || []).filter(p => rolePerms.includes(p));
                                const hasOverrides = actualGrants.length > 0 || actualDenials.length > 0;
                                
                                return (
                                    <tr key={u.uid} className={`hover:bg-gray-50 ${u.role === 'disabled' ? 'text-gray-400 bg-gray-50' : 'text-gray-700'}`}>
                                        <td className="px-4 py-3 text-sm">
                                            {getFullName(u) || '-'}
                                            {u.role === 'disabled' && (
                                                <span className="ml-2 text-xs px-1.5 py-0.5 bg-red-100 text-red-700 rounded font-medium">Deactivated</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-sm">{u.displayName || '-'}</td>
                                        <td className="px-4 py-3 text-sm">{u.email || '-'}</td>
                                        <td className="px-4 py-3 text-sm">{u.phone || '-'}</td>
                                        <td className="px-4 py-3 text-sm">{u.title || '-'}</td>
                                        <td className="px-4 py-3 text-sm">{u.department || '-'}</td>
                                        <td className="px-4 py-3 text-sm">
                                            {u.role === 'disabled' ? (
                                                <span className="text-red-600 font-medium">Disabled</span>
                                            ) : u.role ? getRoleDisplayName(u.role) : '-'}
                                        </td>
                                        <td className="px-4 py-3 text-sm">
                                            <button
                                                className="text-indigo-600 hover:underline flex items-center gap-1"
                                                onClick={() => handleViewPermissions(u)}
                                            >
                                                <span>{userPerms.length} permissions</span>
                                                {hasOverrides && (
                                                    <span className="text-xs px-1.5 py-0.5 bg-yellow-100 text-yellow-700 rounded">
                                                        Custom
                                                    </span>
                                                )}
                                            </button>
                                        </td>
                                        <td className="px-4 py-3 text-sm space-x-2">
                                            {can(FEATURE_PERMISSIONS.USER_EDIT) && (
                                            <button className="text-indigo-600 hover:underline" onClick={() => handleEdit(u)}>Edit</button>
                                            )}
                                            {onImpersonate && user?.role === 'admin' && u.uid !== user.uid && (
                                                <Tooltip text="View app as this user">
                                                    <button
                                                        className="text-amber-600 hover:underline"
                                                        onClick={() => { onImpersonate(u); navigate('/dashboard'); }}
                                                    >
                                                        View As
                                                    </button>
                                                </Tooltip>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
            {showModal && (
                <UserModal
                    user={user}
                    open={showModal}
                    initial={editUser ? { ...editUser, id: editUser.uid } : null}
                    onClose={handleModalClose}
                />
            )}
            {deactivatingUser && (
                <ReassignmentModal
                    targetUser={deactivatingUser}
                    activeUsers={usersList}
                    onConfirm={handleReassignmentConfirm}
                    onCancel={handleReassignmentCancel}
                />
            )}
            {importing && (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white p-8 rounded shadow text-xl font-semibold flex flex-col items-center">
            Importing users, please wait...
            <button
                className="mt-6 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-500"
                onClick={() => {
                    importCancelledRef.current = true;
                    setImportCancelled(true);
                    setImporting(false);
                    if (importTimeoutId) clearTimeout(importTimeoutId);
                }}
            >
                Cancel Import
            </button>
        </div>
    </div>
)}
            {/* Register Modal */}
            {showRegisterModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                    <div className="bg-white rounded-lg shadow-lg p-6 relative w-full max-w-lg">
                        <button
                            className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 text-xl"
                            onClick={() => setShowRegisterModal(false)}
                            aria-label="Close"
                        >
                            &times;
                        </button>
                        <RegisterForm onSuccess={() => {
                            setShowRegisterModal(false);
                            setToast({ message: 'User created successfully.', type: 'success' });
                        }} />
                    </div>
                </div>
            )}

            {/* Permissions Modal */}
            {showPermissionsModal && selectedUserForPermissions && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
                    <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
                        {/* Modal Header */}
                        <div className="p-4 border-b flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">
                                    Permissions for {selectedUserForPermissions.firstName} {selectedUserForPermissions.lastName}
                                </h3>
                                <p className="text-sm text-gray-600">
                                    Role: <span className="font-medium">
                                        {selectedUserForPermissions.role 
                                            ? getRoleDisplayName(selectedUserForPermissions.role)
                                            : 'User'}
                                    </span>
                                </p>
                            </div>
                            <button
                                onClick={() => {
                                    setShowPermissionsModal(false);
                                    setSelectedUserForPermissions(null);
                                }}
                                className="text-gray-500 hover:text-gray-700 text-2xl"
                                aria-label="Close"
                            >
                                &times;
                            </button>
                        </div>

                        {/* Permission Manager */}
                        <div className="flex-1 overflow-auto">
                            <PermissionManager
                                rolePermissions={allRolePermissions[selectedUserForPermissions.role] || DEFAULT_ROLE_PERMISSIONS[selectedUserForPermissions.role] || []}
                                permissionGrants={selectedUserForPermissions.permissionGrants || []}
                                permissionDenials={selectedUserForPermissions.permissionDenials || []}
                                onPermissionChange={handlePermissionChange}
                                onBulkPermissionChange={handleBulkPermissionChange}
                                isAdmin={can(FEATURE_PERMISSIONS.USER_MANAGE_PERMISSIONS)}
                                showOverrides={true}
                                readOnly={!can(FEATURE_PERMISSIONS.USER_MANAGE_PERMISSIONS)}
                            />
                        </div>

                        {/* Modal Footer */}
                        <div className="p-4 border-t bg-gray-50 flex justify-between items-center">
                            <div className="flex items-center gap-3">
                                <div className="text-sm text-gray-600">
                                    <span className="font-medium">Note:</span> Changes are saved automatically. 
                                    User-specific permissions override their role permissions.
                                </div>
                                {can(FEATURE_PERMISSIONS.USER_MANAGE_PERMISSIONS) && 
                                 ((selectedUserForPermissions.permissionGrants?.length > 0) || 
                                  (selectedUserForPermissions.permissionDenials?.length > 0)) && (
                                    <button
                                        onClick={handleRestoreDefaultPermissions}
                                        className="px-3 py-1.5 text-sm bg-amber-50 text-amber-700 border border-amber-300 rounded hover:bg-amber-100 whitespace-nowrap"
                                    >
                                        Restore Defaults
                                    </button>
                                )}
                            </div>
                            <button
                                onClick={() => {
                                    setShowPermissionsModal(false);
                                    setSelectedUserForPermissions(null);
                                }}
                                className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
};

export default UserAdminPage;